import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import {
  buildSalsaUrl,
  NavigateToMicroproductPayload,
} from '@domain/microproducts/components/micro-product-webview-types';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { MicroProductWebView } from '@domain/microproducts/components/micro-product-webview';
import {
  partnerSupplier,
  partnerSupplierAppURL,
  salsaConfig,
} from '@/utils/environment';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import { useStorefrontMicroProducts } from '@/screens/storefront-microproducts/blocs/use-storefront-microproducts';
import { useStorefrontUrlMaintenance } from '@/screens/storefront-microproducts/blocs/use-storefront-url-maintenance';
import { useUserContext } from '@/contexts/user-context';

// v1 advertises no host routes to the SPA — an operator HOST_ROUTE_MAP is a
// follow-up, so NAVIGATE_HOST requests are simply not offered.
const SUPPORTED_HOST_ROUTES: string[] = [];

// Module-level on purpose: the WebView's message-listener effect depends on
// this callback's identity, and the in-container tab keeps the view mounted
// for the whole session — an inline arrow would tear down and re-register
// the window listener on every re-render (dropping any AUTH_TOKEN_REQUEST
// that lands in the gap).
const handleAuthTokenRequest = () => SessionService.getIdToken();

export interface StorefrontMicroProductViewProps {
  onNavigateToMicroproduct: (payload: NavigateToMicroproductPayload) => void;
  productId?: string;
}

/**
 * Renders a salsa microproduct on the storefront (operator portal) surface:
 * loading / not-found states and the authenticated WebView.
 *
 * Navigation-agnostic on purpose — it is shared by the in-container tab
 * (large/tablet, D9) and the pushed stack screen (small layout), which wrap
 * it in their own chrome and own how cross-microproduct navigation happens.
 * Microproducts own their page chrome, so this renders flush; the SPA's own
 * PageLayout provides the title and actions.
 */
export const StorefrontMicroProductView: React.FC<
  StorefrontMicroProductViewProps
> = ({ onNavigateToMicroproduct, productId }) => {
  // Guarded URL maintenance (web only; inert on native via the platform
  // split): /storefront/:productId while focused, / on blur. Living here —
  // not in the surfaces — means the in-container tab and the small-layout
  // stack screen maintain the URL identically.
  useStorefrontUrlMaintenance(productId);

  const { loading, products } = useStorefrontMicroProducts();
  const { selectedRestaurantUUID } = useUserContext();
  const product = products.find((p) => p.id === productId) ?? null;

  if (loading) {
    return (
      <View
        style={styles.loadingContainer}
        testID="storefront-microproduct-loading"
      >
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (!product) {
    return (
      <EmptyStateMinimal
        text={i18n.t('storefront_microproducts.not_found.title')}
      />
    );
  }

  const uri = buildSalsaUrl({
    businessOrganizationId: partnerSupplier.businessOrganizationUUID,
    embedContext: {
      restaurantUUID: selectedRestaurantUUID,
      surface: 'storefront',
    },
    extraParams: product.props,
    forwardedHost: partnerSupplierAppURL,
    locale: i18n.locale,
    platform: Platform.OS as 'web' | 'ios' | 'android',
    productId: product.id,
    salsaBaseUrl: salsaConfig.webUrl,
  });

  return (
    <MicroProductWebView
      onAuthTokenRequest={handleAuthTokenRequest}
      onNavigateToMicroproduct={onNavigateToMicroproduct}
      supportedHostRoutes={SUPPORTED_HOST_ROUTES}
      uri={uri}
    />
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
});
